import { R, type Ramp, type Toy } from "./toy.ts";

/** Shared face and accessory parts for creature painters (creatures.ts, frontier.ts). */
// ---------------------------------------------------------------------------
// face + accessory library
// ---------------------------------------------------------------------------

export const INK = R.ink;
export const WHITE = R.white;

/**
 * Big glossy toy eye, 4×5, top-left at (x, y). The dark mass is the pupil; the gloss is one hard
 * white pixel on the key side stepping down to cream beside it, over an iris crescent along the
 * bottom. The old build put two whites on the diagonal, which at 32px read as a scratch across the
 * eye and at 16px averaged the whole bead pale — this keeps a dark pupil under a crisp catch.
 */
export function eyeBig(t: Toy, x: number, y: number, iris: Ramp = R.blue): void {
  t.rect(x + 1, y, 2, 1, INK, 1);
  t.rect(x, y + 1, 4, 3, INK, 1);
  t.rect(x + 1, y + 4, 2, 1, INK, 1);
  // Iris crescent below the pupil: the species colour, never brighter than the catch.
  t.px(x + 1, y + 3, iris, 1);
  t.px(x + 2, y + 3, iris, 2);
  // Catchlight: white, then one hard step to cream away from the key. Two pixels, one shape.
  t.px(x + 1, y + 1, WHITE);
  t.px(x + 2, y + 1, R.cream, 1);
}

/** 3×3 bead eye: corner catch stepping to cream, dark core kept dark. */
export function eyeBead(t: Toy, x: number, y: number): void {
  t.rect(x, y, 3, 3, INK, 1);
  t.px(x, y, WHITE);
  t.px(x + 1, y, R.cream, 1);
}

/**
 * Glossy eye for a face set into a recess — a hollow hat, a helmet slot. The standard ink sclera
 * vanishes into the cavity it floats in, so this one inverts: a cream bead, a dark pupil set
 * toward the key light, and a hard white catch on the bead's lit shoulder.
 */
export function eyeHollow(t: Toy, x: number, y: number): void {
  t.ell(x, y, 2.4, 2.2, R.cream, { spec: false, occ: false });
  t.px(x - 2, y - 1, WHITE);
  t.rect(x, y - 1, 2, 3, INK, 1);
  t.px(x, y - 1, R.paper, 0);
}

/** 2×2 dot eye. */
export function eyeDot(t: Toy, x: number, y: number): void {
  t.rect(x, y, 2, 2, INK, 1);
  t.px(x, y, WHITE);
}

/** Half-lidded sly eye, 3 wide. */
export function eyeSleepy(t: Toy, x: number, y: number): void {
  t.hline(x, y, 3, INK, 1);
  t.rect(x, y + 1, 3, 2, INK, 1);
  t.px(x + 2, y + 1, WHITE);
}

/** Angry brow over an eye whose top-left is (x, y); `dir` +1 slants down toward the centre. */
export function brow(t: Toy, x: number, y: number, dir: 1 | -1): void {
  t.px(x, y - 1 - (dir > 0 ? 1 : 0), INK, 1);
  t.px(x + 1, y - 1, INK, 1);
  t.px(x + 2, y - 1 - (dir > 0 ? 0 : 1), INK, 1);
}

/** Glowing visor band with two laser points. */
export function visor(t: Toy, x: number, y: number, w: number): void {
  t.rect(x, y, w, 3, R.slate, 1);
  t.hline(x + 1, y + 1, w - 2, R.blue, 1);
  t.px(x + 2, y + 1, R.blue, 0);
  t.px(x + w - 3, y + 1, R.blue, 0);
  t.px(x + Math.floor(w / 2) - 3, y + 1, R.rose, 1);
  t.px(x + Math.floor(w / 2) + 2, y + 1, R.rose, 1);
  t.px(x + 1, y, WHITE);
  t.px(x + w - 2, y, WHITE);
}

export function nose(t: Toy, x: number, y: number): void {
  t.hline(x, y, 2, INK, 1);
}

/** Small smile, w = 3 or 5. */
export function smile(t: Toy, x: number, y: number, w = 3): void {
  t.px(x, y, INK, 1);
  t.hline(x + 1, y + 1, w - 2, INK, 1);
  t.px(x + w - 1, y, INK, 1);
}

export function frown(t: Toy, x: number, y: number, w = 4): void {
  t.hline(x, y, w, INK, 1);
}

/** Open mouth with a rose inside and white teeth along the top. */
export function grin(t: Toy, x: number, y: number, w: number, h: number): void {
  t.rect(x, y, w, h, INK, 1);
  if (h >= 3) t.rect(x + 1, y + 1, w - 2, h - 2, R.rose, 2);
  for (let i = 1; i < w - 1; i += 2) t.px(x + i, y, WHITE);
}

/** Toothy grin line with alternating teeth. */
export function teeth(t: Toy, x: number, y: number, w: number): void {
  t.hline(x, y, w, INK, 1);
  for (let i = 1; i < w - 1; i += 2) t.px(x + i, y, WHITE);
}

/** Pointed ear with apex at (ax, ay) and a base centred on (bx, by). */
export function earPoint(t: Toy, bx: number, by: number, ax: number, ay: number, fur: Ramp, inner?: Ramp, half = 3): void {
  t.poly([[bx - half, by], [bx + half, by], [ax, ay]], fur, { grad: true });
  if (inner) t.poly([[bx - half + 1.5, by], [bx + half - 1.5, by], [ax, ay + (by - ay) * 0.35]], inner, { tone: 1 });
}

/** Two paws on the baseline. */
export function paws(t: Toy, cx: number, spread: number, r: number, fur: Ramp, o: { shade?: number } = {}): void {
  for (const x of [cx - spread, cx + spread]) {
    t.ell(x, 27.6, r, r * 0.75, fur, { spec: false, shade: o.shade });
    t.darken(Math.round(x) - 1, 29);
    t.darken(Math.round(x) + 1, 29);
  }
}

export function crown(t: Toy, cx: number, y: number, w: number): void {
  const x0 = Math.round(cx - w / 2);
  t.rect(x0, y + 2, w, 2, R.gold, 1);
  t.hline(x0 + 1, y + 2, w - 2, R.gold, 0);
  t.vline(x0, y, 2, R.gold, 0);
  t.vline(x0 + w - 1, y, 2, R.gold, 0);
  t.px(Math.round(cx) - (w % 2 ? 0 : 1), y, R.gold, 0);
  t.px(Math.round(cx) - (w % 2 ? 0 : 1), y + 1, R.gold, 0);
  t.px(Math.round(cx) - (w % 2 ? 0 : 1), y + 3, R.rose, 1);
  t.px(x0 + 1, y + 2, WHITE);
  t.px(x0 + w - 2, y + 2, WHITE);
}

/** Candle flame, tip at (x, y), height h. */
export function flame(t: Toy, x: number, y: number, h: number): void {
  t.poly([[x - 1.5, y + h], [x + 1.5, y + h], [x, y]], R.gold, { tone: 1 });
  t.vline(x, y + 1, Math.max(1, h - 2), R.gold, 0);
  t.px(x, y + 1, WHITE);
  t.px(x, y + h - 1, WHITE);
}

/** Front arc of a ring (angles in radians, screen space). */
export function arc(t: Toy, cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, ramp: Ramp, tone = 0): void {
  const steps = Math.max(16, Math.round((rx + ry) * 3));
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    t.px(Math.round(cx + Math.cos(a) * rx), Math.round(cy + Math.sin(a) * ry), ramp, tone);
  }
}

/**
 * Hollow bar volume — padlock shackle, bell yoke, counter handle. A shaded ellipsoid with its
 * core erased, so the hole reads as a real hole (the outline pass rings it in ink) and the arc's
 * underside keeps its own shadow instead of glowing like a lit gap.
 */
export function hoop(t: Toy, cx: number, cy: number, rx: number, ry: number, thick: number, ramp: Ramp): void {
  t.ell(cx, cy, rx, ry, ramp);
  const ix = Math.max(0.6, rx - thick), iy = Math.max(0.6, ry - thick);
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      const nx = (x - cx) / ix, ny = (y - cy) / iy;
      if (nx * nx + ny * ny <= 1) t.erase(x, y);
    }
  }
  const lip = Math.floor(cy - iy);
  for (let x = Math.ceil(cx - ix); x <= Math.floor(cx + ix); x++) {
    t.darken(x, lip);
    t.darken(x, lip - 1);
  }
}

/** Glossy toy eye with a lit iris that reads as a glow: ink pupil, white catchlight, 2px iris. */
export function eyeGlow(t: Toy, x: number, y: number, iris: Ramp): void {
  eyeBig(t, x, y, iris);
  t.px(x + 1, y + 3, iris, 0);
  t.px(x + 2, y + 3, iris, 0);
}

/**
 * Thin lit bar — fence picket, fry, pencil, scroll dowel. Too narrow for the rig's own rim and
 * specular, so the shaded barrel gets a hand-stepped key-side stripe and a white tip kiss.
 */
export function slat(t: Toy, x0: number, y0: number, x1: number, y1: number, r: number, ramp: Ramp, tip = true): void {
  t.cap(x0, y0, x1, y1, r, ramp, { shade: 1, spec: false });
  t.cap(x0 - 0.35, y0 - 0.35, x1 - 0.35, y1 - 0.35, Math.max(0.4, r - 0.95), ramp, { flat: 0, occ: false });
  if (tip) t.px(Math.round(x1), Math.round(y1), WHITE);
}

/** Padlock keyhole: a bored round well over a tapered slot. Reads as one dark shape at 16px. */
export function keyhole(t: Toy, cx: number, y: number, r: number): void {
  t.ell(cx, y + r, r, r, INK, { flat: 1, occ: false });
  t.poly([[cx - r * 0.6, y + r], [cx + r * 0.6, y + r], [cx + r * 1.1, y + r * 3], [cx - r * 1.1, y + r * 3]], INK, { tone: 1 });
  t.px(Math.round(cx - r * 0.45), Math.round(y + r * 0.5), R.slate, 0);
}

/**
 * Tapered blade volume — horn, fin, reply arrow, ear spike. A `poly` of this size gets no rim or
 * specular from the rig and reads as a flat paper cutout, so the taper is marched by hand: a lit
 * leading edge on whichever side faces the key light, a mid face, a shaded trailing face, and a
 * white kiss on the tip. `half` is the half-width at the base; the tip closes to a point.
 */
export function blade(t: Toy, bx: number, by: number, tx: number, ty: number, half: number, ramp: Ramp): void {
  const dx = tx - bx, dy = ty - by;
  const len = Math.max(1, Math.hypot(dx, dy));
  const ux = dx / len, uy = dy / len;
  const nx = -uy, ny = ux;
  // +n or −n, whichever perpendicular leans into the upper-left key light.
  const keyed = nx * -0.55 + ny * -0.62 > 0 ? 1 : -1;
  for (let s = 0; s <= len; s += 0.4) {
    const w = half * (1 - s / (len + 0.8));
    const cx = bx + ux * s, cy = by + uy * s;
    for (let o = -w; o <= w; o += 0.4) {
      const side = o * keyed;
      t.px(cx + nx * o, cy + ny * o, ramp, side > w * 0.3 ? 0 : side < -w * 0.3 ? 2 : 1);
    }
  }
  // A tip in the air glints; a tip planted on the floor takes its contact shadow instead.
  if (ty >= 28) t.darken(tx, ty, 2);
  else t.px(tx, ty, WHITE);
  // The keel: a blade throws a shadow onto the volume it grows out of.
  t.darken(Math.round(bx - nx * half * keyed), Math.round(by - ny * half * keyed));
}

/**
 * Speech bubble prop: a glossy paper slab with two printed lines and a solid wedge tail pointing
 * back at whoever is talking. `dir` is the side the speaker is on. The tail is a wedge rather than
 * a hairline so the bubble still reads as speech when the icon halves it.
 */
export function bubble(t: Toy, cx: number, cy: number, rx: number, ry: number, dir: 1 | -1): void {
  t.slab(cx, cy, rx, ry, Math.min(rx, ry) * 0.6, R.paper);
  t.poly([
    [cx + dir * (rx - 0.5), cy + ry * 0.1],
    [cx + dir * (rx - 0.5), cy + ry * 0.95],
    [cx + dir * (rx + 2), cy + ry * 1.25],
  ], R.paper, { tone: 2 });
  t.px(cx + dir * (rx + 1), cy + ry * 0.9, R.paper, 1);
  const w = Math.max(2, Math.round(rx * 1.1));
  for (let i = 0; i < (ry >= 3 ? 2 : 1); i++) {
    t.hline(Math.round(cx - w / 2), Math.round(cy - 1 + i * 2), w, R.slate, 1);
  }
}

/** 1px metal stud: a lit crown with its own contact shadow. */
export function rivet(t: Toy, x: number, y: number, ramp: Ramp): void {
  t.px(x, y, ramp, 0);
  t.darken(x + 1, y + 1);
}

